import React from 'react';
import { Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import { faWarehouse, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm, FlexCenterRow, FormDivider, onFieldChange, onReactSelectChanged,
    Input, ToastMessage,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext, { ApiRoutes, AppNavPaths, ServiceResponseResult } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { SaveButton } from '../common/forms/SaveButton';
import { CompanyBillingGroup } from './CompanyBillingGroup';

class CompanyBillingGroupForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            companyBillingGroup: new CompanyBillingGroup(),
            companies: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;

        this.onReactSelectChanged = onReactSelectChanged.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (prevProps && (this.props.match.params.id !== (prevProps.match.params ?? {}).id)) {
            this.populateState();
        }
    }

    onChange = onFieldChange;

    onClearErrors = () => this.setState({ errors: {} });

    onSubmit = async () => {
        // Clear any fluent api errors
        this.onClearErrors();
        this.setSaving(true);

        if (this.props.location.pathname == AppNavPaths.CompanyBillingGroupNew) {
            await this.create();
        } else {
            await this.update();
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    create = async () => {
        const { companyBillingGroup } = this.state;
        companyBillingGroup.id = 0;
        const resp = await httpClient.post(ApiRoutes.companyBillingGroups.create(), companyBillingGroup)
            .catch(this.handleSaveError);
        if (resp) {
            this.onBillingGroupsaved(resp);
        }
        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    async populateState() {
        const companyBillingGroupId = this.props.match.params.id;

        const companyBillingGroup = companyBillingGroupId ? await httpClient.js(ApiRoutes.companyBillingGroups.byId(companyBillingGroupId)) : new CompanyBillingGroup();
        const companies = await httpClient.js(ApiRoutes.company.invoicing());
        const originalData = clone(companyBillingGroup);

        this.setState({
            originalData,
            companyBillingGroup,
            companies,
            loading: false,
            saving: false,
        });
    }

    resetForm = () => this.setState({ formValidated: false });

    update = async () => {
        const { companyBillingGroup } = this.state;

        const resp = await httpClient.put(ApiRoutes.companyBillingGroups.update(companyBillingGroup.id), companyBillingGroup)
            .catch(this.handleSaveError);
        if (resp) {
            this.onBillingGroupsaved(resp);
        }
        this.setSaving(false);
    };

    onBillingGroupsaved = (response) => {
        if (response.result === ServiceResponseResult.Ok) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message="Saved."
                />,
            );
            this.context.history.push(AppNavPaths.CompanyBillingGroup);
        } else {
            toast.error(
                <ToastMessage
                    icon={faExclamationTriangle}
                    header="There was a problem saving"
                    message={`${response.message}`}
                />,
            );
        }
    };

    render() {
        const {
            companyBillingGroup,
            companies,
            originalData,
            errors,
            formValidated,
            saving,
        } = this.state;

        const existing = !!companyBillingGroup.id;

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, companyBillingGroup)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    backUrl={AppNavPaths.CompanyBillingGroup}
                    backLabel="Back to Groups"
                    formShown={this.context.formIsOpen}
                    formId="groupForm"
                    formHeadingIcon={faWarehouse}
                    formHeading={!companyBillingGroup.id ? 'New Company Billing Group' : 'Edit Company Billing Group'}
                    formName="groupForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    onClearErrors={this.onClearErrors}
                    loading={this.state.loading}
                >
                    <br />
                    <Row className="pt-3">
                        <Col
                            xl="6"
                            lg="8"
                            md="10"
                            sm="12"
                            className="ml-auto mr-auto"
                        >
                            <Input
                                label="Name"
                                id="description"
                                name="companyBillingGroup.description"
                                value={companyBillingGroup.description ?? ''}
                                onChange={this.onChange}
                                placeholder="Enter Name (max 150 characters)"
                                maxLength="150"
                                pattern="[^()/><\][\\\x22,;|]+"
                                error="Name is required and can only contain the following special characters: hyphens and periods."
                                required
                            />

                            <ValidatedSelect
                                id="companyBillingGroupsIds"
                                name="companyBillingGroup.companyBillingGroupsIds"
                                label="Companies"
                                required
                                isMulti
                                options={companies}
                                value={
                                    (companies ?? []).filter((x) =>
                                        (companyBillingGroup.companyBillingGroupsIds ?? []).includes(x.value),
                                    )
                                }
                                onChange={this.onReactSelectChanged}
                                error="A company is required."
                            />
                        </Col>
                    </Row>
                    <FormDivider />

                    <FlexCenterRow className="mb-3">
                        <SaveButton
                            saving={!!this.state.saving}
                            label={!existing ? 'Save New Group' : 'Save'}
                        />
                    </FlexCenterRow>

                </AppPageForm>
            </>
        );
    }
}
export default CompanyBillingGroupForm;
